import { ShieldCheck, Award, FileCheck, HardHat } from "lucide-react";
import { Link } from "react-router-dom";
import SEO, { localBusinessSchema, breadcrumbSchema } from "../components/SEO";
import { SectionLabel, Breadcrumbs, CTASection } from "../components/Shared";
import { IMAGES, PROCESS_PHASES } from "../data/siteData";

const crumbs = [{ name: "Home", path: "/" }, { name: "About", path: "/about" }];

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About Vantyx Coatings | Phoenix's Premier Garage Floor Company"
        description="Vantyx Coatings is a Phoenix-based, licensed and insured garage floor coating company obsessed with quality. Learn why we built TRI-BOND™ and refuse to install epoxy."
        path="/about"
        ogImage={IMAGES.founder}
        schemas={[localBusinessSchema(), breadcrumbSchema(crumbs)]}
      />

      <section className="hero-gradient py-20 md:py-28 px-6 md:px-12 grain relative" data-testid="about-hero">
        <div className="max-w-7xl mx-auto">
          <Breadcrumbs items={crumbs} />
          <SectionLabel>Our Story</SectionLabel>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white max-w-4xl">
            Built in Phoenix. <span className="text-[#028090]">Obsessed With Floors That Last.</span>
          </h1>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <SectionLabel>The Founder Story</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#000000] mb-6">We got tired of watching Phoenix floors fail.</h2>
            <div className="space-y-5 text-gray-600 leading-relaxed">
              <p>Vantyx Coatings started the way a lot of good companies do: with frustration. We kept seeing the same story across the Valley — homeowners paying real money for garage floors that yellowed by year two and peeled by year three. Box-store epoxy kits. Acid-etch shortcuts. "Lifetime warranties" from companies that vanished by summer.</p>
              <p>So we built the company we wished existed. One system — TRI-BOND™ — engineered specifically for Arizona slabs and Arizona heat. One standard: diamond-grind every square foot, repair every crack, full flake broadcast every time, UV-stable polyaspartic top to bottom. No good-enough. No shortcuts a customer can't see.</p>
              <p>We're Phoenix-based, licensed, insured, and we answer our own phone. When we say lifetime limited warranty, we mean a document you can hold — backed by people who live in the same heat you do.</p>
            </div>
          </div>
          <img src={IMAGES.founder} alt="vantyx coatings founder phoenix garage floor coating expert" loading="lazy" className="w-full object-cover aspect-[4/5] teal-glow" />
        </div>
      </section>

      <section className="bg-[#000000] py-24 md:py-32 px-6 md:px-12" data-testid="about-why-section">
        <div className="max-w-5xl mx-auto">
          <SectionLabel>The Engineering Choice</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6">Why TRI-BOND™ — and not epoxy</h2>
          <p className="text-gray-300 leading-relaxed mb-5">Epoxy is cheaper to buy and easier to install, which is exactly why most companies push it. But in Phoenix, epoxy yellows under UV, softens under hot tires, and takes most of a week to cure. We refuse to install a product we'd have to apologize for in two years.</p>
          <p className="text-gray-300 leading-relaxed">Polyaspartic — the chemistry behind TRI-BOND™ — was engineered for bridges and parking decks in full sun. It never yellows, cures in hours, flexes instead of chipping, and bonds ferociously to diamond-ground concrete. It costs us more per gallon. It's worth every cent. <Link to="/blog/polyaspartic-vs-epoxy-garage-floor-coating-phoenix" className="text-[#028090] underline underline-offset-2">Read the full comparison</Link>.</p>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32 px-6 md:px-12" data-testid="about-process-section">
        <div className="max-w-7xl mx-auto">
          <SectionLabel>Step by Step</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#000000] mb-14">Our process, start to finish</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {PROCESS_PHASES.map((p) => (
              <div key={p.num} className="border border-gray-200 p-8 hover:border-[#028090] transition-colors duration-300">
                <span className="font-display text-5xl font-extrabold text-[#028090]/25">{p.num}</span>
                <h3 className="font-display text-xl font-bold text-[#000000] mt-4 mb-3">{p.name}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-16 px-6 md:px-12" data-testid="about-certifications">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { icon: ShieldCheck, label: "Licensed Contractor" },
            { icon: FileCheck, label: "Fully Insured" },
            { icon: Award, label: "Lifetime Warranty" },
            { icon: HardHat, label: "Factory-Trained Crews" },
          ].map((c) => (
            <div key={c.label} className="flex flex-col items-center gap-3">
              <c.icon size={32} className="text-[#028090]" strokeWidth={1.5} />
              <span className="text-[#000000] font-semibold text-sm">{c.label}</span>
            </div>
          ))}
        </div>
      </section>

      <CTASection title="Meet Us in Your Garage" subtitle="Free in-home estimates across the Phoenix Metro. We bring samples, you get a fixed written price." />
    </>
  );
}
